import { create } from "zustand";
import { ACCENTS, DEFAULT_ACCENT, isBrandThemeAccent, loadStoredAccent, saveAccent } from "../../constants/accents.js";
import { DEFAULT_TYPOGRAPHY } from "../../constants/typography.js";
import { applyBrandThemeToDocument, buildBrandColors, resolveBrandTheme } from "../../themes/index.js";

const DEFAULT_CHROME = {
  radiusSm: "6px",
  radiusMd: "8px",
  radiusLg: "10px",
  shadowSm: "0 2px 8px rgba(0,0,0,.08)",
  shadowLg: "0 4px 16px rgba(0,0,0,.1)",
  shadowPopover: "0 8px 32px rgba(0,0,0,.09)",
};

function buildDefaultColors(dark, accent) {
  return {
    bg: dark ? "#181C2E" : "#F6F4F0",
    sb: dark ? "#1E2238" : "#FFFFFF",
    sbBdr: dark ? "#2E3348" : "#EBEBEB",
    hdr: dark ? "#1E2238" : "#FFFFFF",
    card: dark ? "#252A42" : "#FFFFFF",
    cardBdr: dark ? "#3A4058" : "#EDEBE5",
    text: dark ? "#E8EAED" : "#18160E",
    mid: dark ? "#9AA0A6" : "#5E5A50",
    sub: dark ? "#80868B" : "#A09878",
    hover: dark ? "#2E3348" : "#F5F3EE",
    divider: dark ? "#3A4058" : "#F0EDE6",
    aLight: dark ? "#3D1A28" : accent.activeBg,
  };
}

function applyDefaultThemeToDocument(accent, dark, c, typography = DEFAULT_TYPOGRAPHY) {
  const r = document.documentElement;
  r.style.setProperty("--nm-accent", accent.hex);
  r.style.setProperty("--nm-accent-tint", c.aLight);
  r.style.setProperty("--nm-bg", c.bg);
  r.style.setProperty("--nm-sb", c.sb);
  r.style.setProperty("--nm-sb-bdr", c.sbBdr);
  r.style.setProperty("--nm-text", c.text);
  r.style.setProperty("--nm-mid", c.mid);
  r.style.setProperty("--nm-sub", c.sub);
  r.style.setProperty("--nm-hover", c.hover);
  r.style.setProperty("--nm-divider", c.divider);
  r.style.setProperty("--nm-radius-sm", DEFAULT_CHROME.radiusSm);
  r.style.setProperty("--nm-radius-md", DEFAULT_CHROME.radiusMd);
  r.style.setProperty("--nm-font", typography.fontFamily);
  r.style.setProperty("--nm-font-display", typography.fontFamilyDisplay);
  r.style.setProperty("--nm-font-mono", typography.fontFamilyMono);
  r.style.colorScheme = dark ? "dark" : "light";
  document.body.style.fontFamily = typography.fontFamily;
  document.body.style.background = c.bg;
  document.body.style.color = c.text;
}

function computeThemeState(accent, dark) {
  const brandTheme = resolveBrandTheme(accent);
  const c = brandTheme ? buildBrandColors(brandTheme, dark) : buildDefaultColors(dark, accent);
  const chrome = brandTheme?.chrome ?? DEFAULT_CHROME;
  const typography = brandTheme?.typography ?? DEFAULT_TYPOGRAPHY;
  return {
    accent,
    dark,
    c,
    accentHex: accent.hex,
    isBrandTheme: isBrandThemeAccent(accent),
    brandTheme,
    chrome,
    typography,
  };
}

const initialAccent = loadStoredAccent();
const initialDark = false;
const initial = computeThemeState(initialAccent, initialDark);

export const useThemeStore = create((set, get) => ({
  ...initial,

  setAccent: (a) => {
    if (!a?.name || a.name === get().accent.name) return;
    saveAccent(a);
    set(computeThemeState(a, get().dark));
    get().applyCssVars();
  },

  setDark: (dark) => {
    set(computeThemeState(get().accent, dark));
    get().applyCssVars();
  },

  toggleDark: () => {
    get().setDark(!get().dark);
  },

  applyCssVars: () => {
    const { accent, dark, c, brandTheme, typography } = get();
    if (brandTheme) {
      applyBrandThemeToDocument(brandTheme, dark, c);
    } else {
      applyDefaultThemeToDocument(accent, dark, c, typography);
    }
  },
}));

useThemeStore.getState().applyCssVars();

export const useThemeColors = () => useThemeStore((s) => s.c);
export const useAccentHex = () => useThemeStore((s) => s.accentHex);
export const useThemeChrome = () => useThemeStore((s) => s.chrome);
export const useThemeTypography = () => useThemeStore((s) => s.typography);
export const useIsDark = () => useThemeStore((s) => s.dark);
